import { useCallback, useRef } from 'react'

import { debounce, get, map } from 'lodash'

import api from 'common/api'
import { FIELDS } from 'common/metadata-constants'

import { types } from '../../../../../AwesomeBar'
import ResultComponent from './ResultComponent'

interface ICollege {
  name: string
  tokens: [string, string, string, string]
  value: string
}

export const label = 'Colleges'

const onSearch = async (term: string): Promise<ICollege[]> => {
  return (await api.get<ICollege[]>(`/api/search/college/${encodeURIComponent(term)}?size=50`)) ?? []
}

const debouncedOnSearch = debounce(
  async (
    term: string,
    resolve: (value: ICollege[] | PromiseLike<ICollege[]>) => void,
    reject: (reason?: any) => void,
  ) => {
    try {
      const colleges = await onSearch(term)
      resolve(colleges)
    } catch (error) {
      reject(error)
    }
  },
  300,
)

const getCollegeResult = (term: string, college: ICollege, locationIndex: number) => ({
  name: get(college, 'name'),
  city: get(college, 'tokens[2]'),
  state: get(college, 'tokens[3]'),
  scid: parseInt(get(college, 'value'), 10),
  additionalMetadata: {
    [FIELDS.LOCATION_INDEX]: locationIndex,
    [FIELDS.SEARCH_TERM]: term,
  },
  label: get(college, 'name'),
  searchTypeLabel: label,
})

export const useCollegesSearchConfig = (): types.ISearchConfiguration => {
  // Track the last term that returned no results. If the user keeps typing
  // beyond that term - skip the API call — it won't return anything.
  // Reset when the empty return term changes.
  const lastEmptyTermRef = useRef<string | null>(null)

  const search = useCallback((term: string): Promise<unknown> => {
    if (lastEmptyTermRef.current !== null && term.startsWith(lastEmptyTermRef.current)) {
      return Promise.resolve([])
    }
    lastEmptyTermRef.current = null

    return new Promise((resolve, reject) => {
      debouncedOnSearch(
        term,
        (colleges) => {
          const results = map(colleges as ICollege[], (college, index: number) =>
            getCollegeResult(term, college, index),
          )
          if (results.length === 0) lastEmptyTermRef.current = term
          resolve(results)
        },
        reject,
      )
    })
  }, [])

  return {
    label,
    search,
    ResultComponent,
    allLimit: 20,
    placeholderOption: 'Search for colleges',
    hidden: false,
  }
}
